// lib/use-app-version.ts — detects when the loaded bundle is out of date
// relative to what the backend is currently serving.
//
// How it works: docker-compose.yml passes --build-arg GIT_SHA=<sha> into
// BOTH the backend and the frontend images. The backend's /api/ response
// (apps/backend/src/index.ts) returns process.env.GIT_SHA, and the frontend
// bundle inlines the same value as the global `__APP_VERSION__` constant
// (vite.config.ts' define block). When the two diverge the user is running
// a stale bundle and we prompt them to reload — see
// components/version-update-trigger.tsx for the dialog wiring.
//
// 5-minute polling matches the worst-case CVM-updater tick interval; plus a
// re-check when the tab regains focus, so a user who left the app open
// overnight sees the prompt the moment they come back.

import { useEffect, useState } from "react";

type VersionState = {
  outdated: boolean;
  detected: string | null;
};

const POLL_INTERVAL_MS = 5 * 60 * 1000;

// ponytail: VITE_API_URL is the same env var api-client uses. Relative paths
// like "/api" (production, nginx reverse-proxies /api/* on the same origin)
// get prefixed with the page origin; absolute origins (vite dev talking
// directly to backend:3010) are used verbatim. Mirrors buildUrl() in
// lib/api-client.ts so the version-check fetch follows the same routing as
// the rest of the data calls.
function resolveVersionUrl(): string {
  const base = (import.meta.env.VITE_API_URL ?? "").trim();
  if (!base) return `${window.location.origin}/api/`;
  if (base.startsWith("/")) return `${window.location.origin}${base.replace(/\/$/, "")}/`;
  return `${base.replace(/\/$/, "")}/`;
}

async function fetchServerVersion(signal: AbortSignal): Promise<string | null> {
  try {
    const res = await fetch(resolveVersionUrl(), { cache: "no-store", signal });
    if (!res.ok) return null;
    const body = (await res.json()) as { version?: unknown };
    return typeof body.version === "string" ? body.version : null;
  } catch {
    return null;
  }
}

export function useAppVersion(clientVersion: string = __APP_VERSION__): VersionState {
  const [state, setState] = useState<VersionState>({ outdated: false, detected: null });

  useEffect(() => {
    const controller = new AbortController();
    let cancelled = false;

    const check = async () => {
      const server = await fetchServerVersion(controller.signal);
      if (cancelled || server === null) return;
      if (server !== clientVersion) {
        setState({ outdated: true, detected: server });
      }
    };

    void check();
    const interval = setInterval(() => void check(), POLL_INTERVAL_MS);
    const onVisibility = () => {
      if (document.visibilityState === "visible") void check();
    };
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      cancelled = true;
      controller.abort();
      clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [clientVersion]);

  return state;
}

export { POLL_INTERVAL_MS };
